import { z } from "zod/v4";

import type { CartTargetingFilter } from "../../dtos/cart-targeting";
import type { ComponentPlacementLocation } from "../../dtos/components";
import {
  findMissingTemplateSlots,
  findUnknownTemplateSlots,
} from "../../dtos/components.template-guard";
import type { ProductFilter } from "../../dtos/experience-product-targeting";
import {
  UpsellCompareAtPriceDisplayList,
  UpsellDiscountTypeList,
  UpsellProductItemSchema,
} from "../../dtos/upsell-product-rules";

// Flat authoring primitives for the onsite-upsell mod; the frontend handler
// expands them to the canonical RPN nodes on `OnsiteUpsellConfig`.

// Numeric-only subset of the persisted `CartTargetingFilterList` (drops the
// set-membership `includes`/`doesNotInclude`, which we model via `include`).
// `satisfies` guards the values against drift from the source enum.
export const UpsellNumericOperatorList = [
  "greaterThan",
  "lessThan",
  "equalTo",
  "greaterThanOrEqual",
  "lessThanOrEqual",
  "between",
] as const satisfies readonly CartTargetingFilter[];
export type UpsellNumericOperator = (typeof UpsellNumericOperatorList)[number];

const numericOperator = z
  .enum(UpsellNumericOperatorList)
  .describe(
    "Numeric comparison: `greaterThan`, `lessThan`, `equalTo`, `greaterThanOrEqual`, `lessThanOrEqual`, or `between` (requires `max`)."
  );

// Product-price targeting persists through the narrower `ProductFilterList`, so
// `productPrice` only offers the two operators that layer can express.
export const UpsellPriceOperatorList = [
  "greaterThan",
  "lessThan",
] as const satisfies readonly ProductFilter[];
export type UpsellPriceOperator = (typeof UpsellPriceOperatorList)[number];

const priceOperator = z
  .enum(UpsellPriceOperatorList)
  .describe("Price comparison: `greaterThan` or `lessThan`.");

// Own richly-described authoring schema for the AI's most error-prone field.
// Structurally identical to the persisted `UpsellDiscount` (handler bridges as-is);
// kept here so AI prose stays off the shared backend/plugin DTO.
export const OnsiteUpsellDiscountSchema = z.object({
  discountType: z
    .enum(UpsellDiscountTypeList)
    .default("none")
    .describe(
      "How the discount is calculated: `percentage` (percent off each product), `fixed` (a fixed currency amount off each product), or `none` (no discount — prefer omitting `discount` entirely)."
    ),
  discountValue: z
    .number()
    .nullish()
    .describe(
      "The discount amount: a percentage (e.g. `15` = 15% off) when `discountType` is `percentage`, or a currency amount (e.g. `5` = $5 off) when `fixed`. Must be greater than 0 when a discount is applied; `null` when `discountType` is `none`."
    ),
  compareAtPriceDisplay: z
    .enum(UpsellCompareAtPriceDisplayList)
    .default("none")
    .describe(
      "Which struck-through reference price to show beside the discounted price: `none` (discounted price only), `compareAtPrice` (the product's compare-at price), or `regularPrice` (the product's regular price)."
    ),
});
export type OnsiteUpsellDiscount = z.infer<typeof OnsiteUpsellDiscountSchema>;

// THEN — which products a rule shows (one source per rule).
export const OnsiteUpsellProductSelectionSchema = z.discriminatedUnion(
  "source",
  [
    z.object({ source: z.literal("recommendations") }),
    z.object({
      source: z.literal("collection"),
      include: z
        .boolean()
        .describe(
          "`true` to show products in this collection, `false` to show products NOT in it."
        ),
      collectionId: z
        .string()
        .describe("The collection id whose products to show."),
    }),
    z.object({
      source: z.literal("tag"),
      include: z
        .boolean()
        .describe(
          "`true` to show products with this tag, `false` to show products without it."
        ),
      tag: z.string().describe("The product tag whose products to show."),
    }),
    z.object({
      source: z.literal("products"),
      // Persisted verbatim as the canonical `productId` action value.
      products: z
        .array(UpsellProductItemSchema)
        .describe(
          "Specific products (each with its selected variants) to show. Authored with the visual-builder product picker."
        ),
    }),
  ]
);
export type OnsiteUpsellProductSelection = z.infer<
  typeof OnsiteUpsellProductSelectionSchema
>;

// IF — a single targeting condition; `refineOnsiteUpsellRules` gates which
// types are valid for the upsell's `location`.
export const UpsellConditionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("products"),
    include: z
      .boolean()
      .describe(
        "`true` to match when the viewed product IS in the list, `false` when it is NOT."
      ),
    productIds: z
      .array(z.string())
      .describe(
        "Product ids to match the viewed product against (pdp upsells only)."
      ),
  }),
  z.object({
    type: z.literal("variants"),
    include: z
      .boolean()
      .describe(
        "`true` to match when the cart CONTAINS one of these variants, `false` when it does NOT (cart upsells only)."
      ),
    variants: z
      .array(
        z.object({
          productId: z
            .string()
            .describe("The product id the variant belongs to."),
          variantId: z
            .string()
            .describe("The variant id to match against the cart's line items."),
        })
      )
      .describe(
        "Product variants matched against the cart's line items (cart upsells only)."
      ),
  }),
  z.object({
    type: z.literal("collection"),
    include: z
      .boolean()
      .describe(
        "`true` to match when the product is in this collection, `false` when it is not."
      ),
    collectionId: z.string().describe("The collection id to match against."),
  }),
  z.object({
    type: z.literal("tag"),
    include: z
      .boolean()
      .describe(
        "`true` to match when the product has this tag, `false` when it does not."
      ),
    tag: z.string().describe("The product tag to match against."),
  }),
  z.object({
    type: z.literal("cartSubtotal"),
    operator: numericOperator,
    value: z
      .number()
      .describe(
        "Currency amount to compare the cart subtotal against. For `between`, the lower bound."
      ),
    max: z
      .number()
      .optional()
      .describe("Upper bound for the `between` operator; omit otherwise."),
  }),
  z.object({
    type: z.literal("cartItemCount"),
    operator: numericOperator,
    value: z
      .number()
      .describe(
        "Item count to compare the cart against. For `between`, the lower bound."
      ),
    max: z
      .number()
      .optional()
      .describe("Upper bound for the `between` operator; omit otherwise."),
  }),
  z.object({
    type: z.literal("productPrice"),
    operator: priceOperator,
    value: z
      .number()
      .describe(
        "Currency amount to compare the viewed product's price against."
      ),
  }),
]);
export type UpsellCondition = z.infer<typeof UpsellConditionSchema>;

// A rule = WHEN (conditions AND-joined; empty = always-on) + WHAT to show.
export const OnsiteUpsellRuleAuthoringSchema = z.object({
  conditions: z
    .array(UpsellConditionSchema)
    .describe(
      "Conditions that must ALL be true (AND) for this rule to apply. Empty = always-on; put an always-on rule last. Allowed `type`s depend on the upsell `location`: on `cart` use `variants`, `collection`, `tag`, `cartSubtotal`, `cartItemCount`; on `pdp` use `products`, `collection`, `tag`, `productPrice`."
    ),
  products: OnsiteUpsellProductSelectionSchema.describe(
    "Which products this rule shows (THEN): `recommendations`, a `collection`, a `tag`, or specific `products`."
  ),
});
export type OnsiteUpsellRuleAuthoring = z.infer<
  typeof OnsiteUpsellRuleAuthoringSchema
>;

const CART_ONLY_CONDITIONS = new Set([
  "variants",
  "cartSubtotal",
  "cartItemCount",
]);
const PRODUCT_ONLY_CONDITIONS = new Set(["products", "productPrice"]);

// Cross-field checks a union-composable object can't express (condition type vs
// `location`, `between` needs `max`). Run in the mod's superRefine + FE handler.
export function refineOnsiteUpsellRules(
  location: ComponentPlacementLocation,
  rules: OnsiteUpsellRuleAuthoring[],
  ctx: z.RefinementCtx
): void {
  rules.forEach((rule, ri) => {
    rule.conditions.forEach((condition, ci) => {
      const path = ["rules", ri, "conditions", ci];
      if (location === "pdp" && CART_ONLY_CONDITIONS.has(condition.type)) {
        ctx.addIssue({
          code: "custom",
          path,
          message: `\`${condition.type}\` is a cart condition and can't be used on a pdp upsell.`,
        });
      }
      if (location === "cart" && PRODUCT_ONLY_CONDITIONS.has(condition.type)) {
        ctx.addIssue({
          code: "custom",
          path,
          message: `\`${condition.type}\` is a product condition and can't be used on a cart upsell.`,
        });
      }
      const isBetween =
        "operator" in condition && condition.operator === "between";
      const max = "max" in condition ? condition.max : undefined;
      if (isBetween && max === undefined) {
        ctx.addIssue({
          code: "custom",
          path: [...path, "max"],
          message: "`between` requires `max` (the upper bound).",
        });
      }
    });
  });
}

/**
 * Placement mode coherence. An upsell mounts EITHER inside the merchant's
 * Intelligems Content theme blocks (`anchorKey`) or beside a CSS
 * selector (`selector` + `insertType`). Both set would make the mount target
 * ambiguous; neither leaves it with nowhere to render.
 */
export function refineOnsiteUpsellPlacement(
  placement: {
    anchorKey?: string;
    selector?: string;
    insertType?: string;
  },
  ctx: z.RefinementCtx
): void {
  if (placement.anchorKey) {
    if (placement.selector || placement.insertType) {
      ctx.addIssue({
        code: "custom",
        path: ["anchorKey"],
        message:
          "`anchorKey` and `selector`/`insertType` are two placement modes; set exactly one.",
      });
    }
    return;
  }
  if (!placement.selector) {
    ctx.addIssue({
      code: "custom",
      path: ["selector"],
      message:
        "Placement needs a `selector` (with `insertType`), or an `anchorKey` naming a theme block.",
    });
    return;
  }
  if (!placement.insertType) {
    ctx.addIssue({
      code: "custom",
      path: ["insertType"],
      message: "`insertType` is required with `selector`.",
    });
  }
}

// Discount coherence: `discountValue` depends on `discountType`. Same two layers.
export function refineOnsiteUpsellDiscount(
  discount: OnsiteUpsellDiscount | null | undefined,
  ctx: z.RefinementCtx
): void {
  if (!discount || discount.discountType === "none") return;
  if (discount.discountValue == null || discount.discountValue <= 0) {
    ctx.addIssue({
      code: "custom",
      path: ["discount", "discountValue"],
      message:
        "`discountValue` must be greater than 0 when a discount is applied.",
    });
    return;
  }
  if (discount.discountType === "percentage" && discount.discountValue > 100) {
    ctx.addIssue({
      code: "custom",
      path: ["discount", "discountValue"],
      message: "A percentage `discountValue` can't exceed 100.",
    });
  }
}

// Template slot checks — reject any slot outside the declared vocabulary
// (catches typos like `{{product.sku}}`), and require every slot to stay present
// (dropping one silently removes that element — e.g. losing the comparison price).
// Viv self-corrects from the message and the manual panel gets the same guard.
export function refineOnsiteUpsellTemplate(
  template: string,
  ctx: z.RefinementCtx
): void {
  const unknown = findUnknownTemplateSlots(template);
  if (unknown.length > 0) {
    ctx.addIssue({
      code: "custom",
      path: ["template"],
      message: `Template references unknown slots: ${unknown
        .map((slot) => `{{${slot}}}`)
        .join(
          ", "
        )}. Allowed: {{product.image}}, {{product.url}}, {{product.title}}, {{product.price}}, {{product.compareAtPrice}}, {{variant-select}}, {{atc}}.`,
    });
  }

  const missing = findMissingTemplateSlots(template);
  if (missing.length > 0) {
    ctx.addIssue({
      code: "custom",
      path: ["template"],
      message: `Template is missing required slots: ${missing
        .map((slot) => `{{${slot}}}`)
        .join(
          ", "
        )}. Keep all 7 slots — each hides itself when empty, so none should ever be removed.`,
    });
  }
}
